import {Controller, useFormContext} from 'react-hook-form';
import {FILES_INPUT} from '../data';
import {AnnouncementFormValues} from '../types';
import RenderFiles from './RenderFiles';
import AppFileInputButton from '../../../../../../components/common/AppFileInputButton';
import {SVG} from '../../../../../../constants/assetLists';
import {useRef} from 'react';

type Props = {
  forForm: 'announcement' | 'task';
  index?: number;
  handleExpandSection?: (section: string) => void;
  expandedSections: string[];
};

function Files({forForm, index, handleExpandSection, expandedSections}: Props) {
  const {control, setValue, getValues, trigger} =
    useFormContext<AnnouncementFormValues>();

  const isExpanded =
    forForm === 'announcement' ||
    expandedSections.includes(`task-files-${index}`);
  const filesFieldName =
    forForm === 'task'
      ? (`tasks.${index}.files` as keyof AnnouncementFormValues)
      : 'files';

  const sectionRef = useRef<HTMLDivElement | null>(null);

  const handleHideShowSection = () => {
    handleExpandSection?.(`task-files-${index}`);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files) return;

    const existingFiles: File[] = getValues(filesFieldName) as File[];

    const newFiles = Array.from(files);
    const fileNames = new Set(existingFiles.map(f => f.name + f.size));
    const uniqueNewFiles = newFiles.filter(
      f => !fileNames.has(f.name + f.size),
    );

    setValue(filesFieldName, [...existingFiles, ...uniqueNewFiles], {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    trigger(filesFieldName);

    e.target.value = '';
    requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
    });
  };

  const handleFileRemove = (index: number) => {
    const value: File[] = getValues(filesFieldName) as File[];

    setValue(
      filesFieldName,
      value.filter((_, i) => i !== index),
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      },
    );
    trigger(filesFieldName);
  };

  return (
    <div className="flex flex-col gap-2">
      {forForm === 'task' && (
        <div className="flex justify-start">
          <div
            className="flex items-center gap-4 cursor-pointer"
            onClick={handleHideShowSection}>
            <div className="w-[24px] h-[24px]">
              <img
                src={SVG.fileIcon}
                alt="icon"
                className="w-[20px] h-[20px] object-contain"
              />
            </div>
            <p className="m-0 text-[20px] font-medium text-black">
              הוספת מסמך/תמונה/וידאו
            </p>
          </div>
        </div>
      )}

      {isExpanded && (
        <Controller
          control={control}
          name={filesFieldName}
          render={({field: {value}}) => {
            return (
              <div ref={sectionRef} className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  {FILES_INPUT.map(item => (
                    <AppFileInputButton
                      key={item.id}
                      id={
                        forForm == 'announcement'
                          ? `${forForm}-${item.id}`
                          : `${forForm}-${item.id}-${index}`
                      }
                      icon={item.icon}
                      accept={item.accept}
                      multiple
                      onChange={handleFileChange}
                    />
                  ))}
                </div>

                <div className="flex flex-col gap-2">
                  {(value as File[])?.map((item, idx) => {
                    return (
                      <RenderFiles
                        key={idx}
                        file={item}
                        index={idx}
                        handleFileRemove={() => handleFileRemove(idx)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
}

export default Files;
